// MarkdownText wraps assistant-ui's MarkdownTextPrimitive with element-level
// overrides so headings, code blocks, inline code, lists, links and tables
// render with sensible spacing inside the assistant message bubble.
//
// We deliberately do NOT pull in syntax-highlighting here — the runtime cost
// (highlight.js / shiki / prismjs all weigh 100+ KB) isn't justified for a
// chat panel that mostly shows shell commands. Code blocks render in the
// muted token panel with monospaced text; that's enough to read them.
//
// The model emits <plan>…</plan> blocks via the system prompt. We don't run
// rehype-raw (CRA-5 + ESM-only rehype packages = build hell), so we
// preprocess them into a Markdown blockquote labeled "📋 Plan" before the
// markdown pipeline sees them. See assistant-ui/markdownPreprocess.js.

import React from "react";
import { MarkdownTextPrimitive } from "@assistant-ui/react-markdown";
import { preprocessPlanBlock } from "./markdownPreprocess";
import { CodeBlock } from "./CodeBlock";

const components = {
  h1: ({ node, ...props }) => (
    <h1 className="tw-mb-2 tw-mt-3 tw-text-base tw-font-semibold tw-text-foreground" {...props} />
  ),
  h2: ({ node, ...props }) => (
    <h2 className="tw-mb-2 tw-mt-3 tw-text-sm tw-font-semibold tw-text-foreground" {...props} />
  ),
  h3: ({ node, ...props }) => (
    <h3 className="tw-mb-1.5 tw-mt-2 tw-text-sm tw-font-semibold tw-text-foreground" {...props} />
  ),
  p: ({ node, ...props }) => (
    <p className="tw-my-1 tw-leading-relaxed tw-text-card-foreground first:tw-mt-0 last:tw-mb-0" {...props} />
  ),
  ul: ({ node, ...props }) => (
    <ul className="tw-my-1 tw-list-disc tw-pl-5 tw-text-card-foreground" {...props} />
  ),
  ol: ({ node, ordered, ...props }) => (
    <ol className="tw-my-1 tw-list-decimal tw-pl-5 tw-text-card-foreground" {...props} />
  ),
  li: ({ node, ordered, ...props }) => <li className="tw-my-0.5" {...props} />,
  a: ({ node, ...props }) => (
    <a
      className="tw-text-copilot-primary tw-underline tw-underline-offset-2 hover:tw-opacity-80"
      target="_blank"
      rel="noopener noreferrer"
      {...props}
    />
  ),
  pre: ({ node, ...props }) => <CodeBlock {...props} />,
  code: ({ node, inline, ...props }) =>
    inline ? (
      <code
        className="tw-rounded tw-bg-muted tw-px-1 tw-py-0.5 tw-font-mono tw-text-[0.85em] tw-text-accent"
        {...props}
      />
    ) : (
      // Explicit foreground color: without it the code text was inheriting
      // a wrong colour from somewhere up the assistant-ui markdown
      // primitive chain, rendering as dark-on-dark inside the block.
      <code className="tw-font-mono tw-text-[0.85em] tw-text-foreground" {...props} />
    ),
  hr: ({ node, ...props }) => (
    <hr className="tw-my-2 tw-border-border" {...props} />
  ),
  blockquote: ({ node, ...props }) => (
    <blockquote
      className="tw-my-2 tw-border-l-2 tw-border-border tw-pl-2 tw-text-muted-foreground"
      {...props}
    />
  ),
  table: ({ node, ...props }) => (
    // tw-table-fixed (table-layout: fixed) is load-bearing for long-content
    // tables: without it, cells size to their content and ignore tw-w-full,
    // so a label/value column pair from the LLM can overflow the bubble.
    <table
      className="tw-my-2 tw-w-full tw-table-fixed tw-border-collapse tw-text-xs"
      {...props}
    />
  ),
  th: ({ node, ...props }) => (
    <th className="tw-border tw-border-border tw-px-2 tw-py-1 tw-text-left tw-font-semibold" {...props} />
  ),
  td: ({ node, ...props }) => (
    <td className="tw-border tw-border-border tw-px-2 tw-py-1" {...props} />
  ),
};

export function MarkdownText() {
  return (
    <MarkdownTextPrimitive
      className="copilot-msg-text copilot-msg-markdown tw-text-sm"
      components={components}
      preprocess={preprocessPlanBlock}
    />
  );
}
